import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MetaTags from "react-meta-tags";
import { withRouter, Link } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import Tooltip from "@material-ui/core/Tooltip";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import Select from "react-select";
import { getSelectedSchemesList } from "store/selected-scheme/actions";
import { isEmpty, uniq } from "lodash";
import moment from "moment";
import { getcyclelist } from "store/cycle/actions";

class Roundural extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Data states - initialize as empty to prevent showing stale data
      selectedSchemeList: [],
      cycleList: [],
      filteredData: [],

      // Filter states
      selectedScheme: "All",
      selectedCycle: "Active",
      nameOptions: ["All"],

      // Loading and organization states
      loading: true,
      dataInitialized: false,
      organization_name: "",

      // User state
      user_id: localStorage.getItem("authUser")
        ? JSON.parse(localStorage.getItem("authUser")).user_id
        : null,

      // Table columns
      feedbackListColumns: this.getTableColumns(),
    };
  }

  getTableColumns = () => [
    {
      text: "ID",
      dataField: "id",
      sort: true,
      hidden: true,
    },
    {
      dataField: "scheme_cycle_info",
      text: "Scheme - Cycle",
      sort: false,
      formatter: (cellContent, row) => {
        const resultStatus = row.result_status?.toLowerCase().trim();
        const isOpen = row?.status?.toLowerCase() === "open";
        const isNotSubmitted =
          resultStatus === "not entered" || resultStatus === "saved";
        const className = this.getTextClass(row);

        return (
          <span className={className}>
            {`${row.scheme_name || "N/A"} - ${row.cycle_no || "N/A"}`}
          </span>
        );
      },
      headerStyle: { width: "100px" },
      style: { width: "25%", textAlign: "left" },
    },
    {
      dataField: "rounds",
      text: "Round",
      sort: true,
      formatter: (cellContent, round) => {
        const resultStatus = round.result_status?.toLowerCase().trim();
        const isOpen = round?.status?.toLowerCase() === "open";
        const isNotSubmitted =
          resultStatus === "not entered" || resultStatus === "saved";
        const className = this.getTextClass(round);

        return <span className={className}>{round.rounds}</span>;
      },
      headerStyle: { width: "100px" },
      style: { width: "10%", textAlign: "center" },
    },
    {
      dataField: "issueDate",
      text: "Issue Date",
      sort: true,
      formatter: (cellContent, round) => {
        const resultStatus = round.result_status?.toLowerCase().trim();
        const issueDate = moment(round.issue_date);
        const isOpen = round?.status?.toLowerCase() === "open";
        const isNotSubmitted =
          resultStatus === "not entered" || resultStatus === "saved";
        const className = this.getTextClass(round);

        return (
          <span className={className}>{issueDate.format("DD-MMM-YY")}</span>
        );
      },
    },
    {
      dataField: "closingDate",
      text: "Closing Date",
      sort: true,
      formatter: (cellContent, round) => {
        const closingDate = moment(round.closing_date);
        const today = moment();
        const daysDiff = closingDate.diff(today, "days");
        const isNearDeadline = daysDiff >= 0 && daysDiff <= 2;
        const colorClass = isNearDeadline ? "text-danger font-weight-bold" : "";
        const resultStatus = round.result_status?.toLowerCase().trim();
        const isOpen = round?.status?.toLowerCase() === "open";
        const isNotSubmitted =
          resultStatus === "not entered" || resultStatus === "saved";
        const className = this.getTextClass(round);

        return (
          <span className={`${colorClass} ${className}`}>
            {closingDate.format("DD-MMM-YY")}
          </span>
        );
      },
    },
    {
      dataField: "result_status",
      text: "Result Status",
      sort: true,
      formatter: (cellContent, round) => {
        const resultStatus = round.result_status?.toLowerCase().trim();
        const isOpen = round?.status?.toLowerCase() === "open";
        const isNotSubmitted =
          resultStatus === "not entered" || resultStatus === "saved";
        const className = this.getTextClass(round);

        return <span className={className}>{round.result_status}</span>;
      },
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      formatter: (cellContent, round) => {
        const resultStatus = round.result_status?.toLowerCase().trim();
        const isOpen = round?.status?.toLowerCase() === "open";
        const isNotSubmitted =
          resultStatus === "not entered" || resultStatus === "saved";
        const className = this.getTextClass(round);

        return <span className={className}>{round.status}</span>;
      },
    },
    {
      dataField: "menu",
      isDummyField: true,
      editable: false,
      text: "Action",
      formatter: (cellContent, round) => {
        // 🚫 No action icons for aborted rounds
        if (round.status === "Aborted") {
          return null;
        }

        const { organization_name } = this.props.match.params;

        return (
          <div className="d-flex justify-content-center gap-3 ml-3">
            {round.status !== "Report Available" && (
              <Tooltip title="Results">
                <Link
                  className="fas fa-file-alt font-size-18 text-success"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!organization_name) {
                      console.error("Invalid organization name");
                      return;
                    }

                    const schemeType = round?.scheme_type?.toLowerCase();
                    let url = `/${organization_name}/${round.id}/${round.participant_id}/participantsResults`;

                    if (schemeType === "identification") {
                      url = `/${organization_name}/${round.id}/${round.participant_id}/microresult`;
                    }

                    this.props.history.push(url);
                  }}
                ></Link>
              </Tooltip>
            )}

            <Tooltip title="History">
              <Link
                className="fas fa-comment font-size-18"
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!organization_name) {
                    console.error("Invalid organization name");
                    return;
                  }
                  const url = `/${organization_name}/result-history/participant/${round.id}`;
                  this.props.history.push(url);
                }}
              ></Link>
            </Tooltip>

            {/* Report Icon and Print Button - Show only if status is 'Report Available' */}
            {round.status === "Report Available" && (
              <Tooltip title="View Report">
                {round.scheme_type === "Quantitative" ? (
                  <Link
                    to={`/${organization_name}/${round.id}/${round.participant_id}/report1_view`}
                    className="fas fa-file-alt text-primary font-size-18"
                  />
                ) : round.scheme_type === "Qualitative" ? (
                  <Link
                    to={`/${organization_name}/${round.id}/${round.participant_id}/qualitative_report_view`}
                    className="fas fa-file-alt text-primary font-size-18"
                  />
                ) : round.scheme_type === "Identification" ? (
                  <Link
                    to={`/${organization_name}/${round.id}/${round.participant_id}/micro-report`}
                    className="fas fa-file-alt text-primary font-size-18"
                  />
                ) : null}
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  async componentDidMount() {
    const { organization_name } = this.props.match.params;
    const { user_id } = this.state;

    this.setState({
      organization_name,
      loading: true,
      dataInitialized: false,
    });

    if (!user_id) {
      console.error("User ID is missing");
      this.setState({ loading: false });
      return;
    }

    try {
      // Fetch both data sources concurrently for faster loading
      await Promise.all([
        this.props.onGetRoundList(user_id),
        this.props.onGetCycleList(user_id),
      ]);

      console.log("Data fetched successfully");
    } catch (error) {
      console.error("Error fetching data:", error);
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps) {
    const { SelectedSchemeList, CycleList, match } = this.props;

    // Handle organization change
    const prevOrg = prevProps.match.params.organization_name;
    const currentOrg = match.params.organization_name;

    if (prevOrg !== currentOrg) {
      this.handleOrganizationChange(currentOrg);
      return;
    }

    // Handle data updates - only process when we have valid new data
    const hasNewSchemeData =
      SelectedSchemeList !== prevProps.SelectedSchemeList &&
      Array.isArray(SelectedSchemeList) &&
      SelectedSchemeList.length > 0;

    const hasNewCycleData =
      CycleList !== prevProps.CycleList &&
      Array.isArray(CycleList) &&
      CycleList.length > 0;

    if (hasNewSchemeData || hasNewCycleData) {
      this.processDataUpdate(SelectedSchemeList, CycleList);
    }
  }

  handleOrganizationChange = (newOrg) => {
    console.log("Organization changed, refreshing data...");

    // Reset state immediately
    this.setState({
      organization_name: newOrg,
      selectedSchemeList: [],
      cycleList: [],
      filteredData: [],
      nameOptions: ["All"],
      selectedScheme: "All",
      selectedCycle: "Active",
      loading: true,
      dataInitialized: false,
    });

    // Fetch new data
    if (this.state.user_id) {
      this.props.onGetRoundList(this.state.user_id);
      this.props.onGetCycleList(this.state.user_id);
    }
  };

  processDataUpdate = (schemeList, cycleList) => {
    const updates = {};

    if (schemeList && schemeList.length > 0) {
      const nameOptions = [
        "All",
        ...uniq(schemeList.map((item) => item.scheme_name)),
      ];
      updates.selectedSchemeList = schemeList;
      updates.nameOptions = nameOptions;
    }

    if (cycleList && cycleList.length > 0) {
      updates.cycleList = cycleList;
    }

    // Only update state once with all changes
    this.setState(
      {
        ...updates,
        loading: false,
        dataInitialized: true,
      },
      this.filterData,
    );
  };

  componentWillUnmount() {
    // Clean up state
    this.setState({
      selectedSchemeList: [],
      cycleList: [],
      filteredData: [],
      selectedScheme: "All",
      selectedCycle: "Active",
      dataInitialized: false,
    });
  }

  handleNameFilterChange = (selectedOption) => {
    const selectedScheme = selectedOption ? selectedOption.value : "All";
    this.setState({ selectedScheme }, this.filterData);
  };

  handleCycleFilterChange = (selectedOption) => {
    const selectedCycle = selectedOption ? selectedOption.value : "Active";
    this.setState({ selectedCycle }, this.filterData);
  };

  filterData = () => {
    const { selectedSchemeList, selectedScheme, selectedCycle } = this.state;

    if (!Array.isArray(selectedSchemeList) || selectedSchemeList.length === 0) {
      this.setState({ filteredData: [] });
      return;
    }

    const filtered = selectedSchemeList.filter((entry) => {
      const schemeMatch =
        selectedScheme === "All" || entry.scheme_name === selectedScheme;
      const cycleMatch =
        selectedCycle === "Active" || entry.cycle_status === selectedCycle;
      return schemeMatch && cycleMatch;
    });

    this.setState({ filteredData: filtered });
  };

  getTextClass = (row) => {
    const resultStatus = row?.result_status?.toLowerCase()?.trim();
    const isOpen = row?.status?.toLowerCase() === "open";
    const isNotEntered =
      resultStatus === "not entered" || resultStatus === "saved";

    // 🔴 Open + Not Entered
    if (isOpen && isNotEntered) {
      return "text-danger font-weight-bold";
    }

    // 🔵 Open + Entered
    if (isOpen && !isNotEntered) {
      return "text-primary font-weight-bold";
    }

    return "";
  };

  render() {
    const { SearchBar } = Search;
    const {
      loading,
      dataInitialized,
      filteredData,
      nameOptions,
      selectedScheme,
      selectedCycle,
      feedbackListColumns,
    } = this.state;

    // Show loading state
    if (loading || !dataInitialized) {
      return (
        <div className="page-content">
          <Container fluid>
            <Row className="justify-content-center">
              <Col lg="12">
                <Card>
                  <CardBody>
                    <div className="text-center mt-5">
                      <h5>Loading rounds...</h5>
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      );
    }

    // Show empty state
    if (filteredData.length === 0) {
      return (
        <div className="page-content">
          <MetaTags>
            <title>Rounds</title>
          </MetaTags>
          <Container fluid>
            <Row className="justify-content-center">
              <Col lg="12">
                <Card>
                  <CardBody>
                    <div className="text-center mt-5">
                      <h5>No rounds available</h5>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      );
    }

    const pageOptions = {
      sizePerPage: 50,
      totalSize: filteredData.length,
      custom: true,
    };

    const cycleOptions = [
      { value: "Active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      ...(Array.isArray(this.props.CycleList)
        ? this.props.CycleList.map((cycle) => ({
            value: cycle.cycle_no,
            label: `${cycle.cycle_no} (${cycle.status})`,
          }))
        : []),
    ];

    return (
      <React.Fragment>
        <div className="page-content">
          <MetaTags>
            <title>Rounds</title>
          </MetaTags>
          <Container fluid>
            <Row className="justify-content-center">
              <Col lg="12">
                <Card>
                  <CardBody>
                    <PaginationProvider
                      pagination={paginationFactory(pageOptions)}
                      keyField="id"
                      columns={feedbackListColumns}
                      data={filteredData}
                    >
                      {({ paginationProps, paginationTableProps }) => (
                        <ToolkitProvider
                          keyField="id"
                          columns={feedbackListColumns}
                          data={filteredData}
                          search
                        >
                          {(toolkitprops) => (
                            <React.Fragment>
                              <Row className="mb-2">
                                <Col xs="4" sm="4" md="3" lg="3">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Select Scheme
                                    </label>
                                    <Select
                                      onChange={this.handleNameFilterChange}
                                      options={nameOptions.map((name) => ({
                                        value: name,
                                        label: name,
                                      }))}
                                      placeholder="Select Scheme..."
                                      isClearable={true}
                                      value={
                                        selectedScheme
                                          ? {
                                              value: selectedScheme,
                                              label: selectedScheme,
                                            }
                                          : null
                                      }
                                    />
                                  </div>
                                </Col>

                                <Col xs="4" sm="4" md="3" lg="3">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Select Cycle
                                    </label>
                                    <Select
                                      onChange={this.handleCycleFilterChange}
                                      options={cycleOptions}
                                      placeholder="Select Cycle..."
                                      isClearable={true}
                                      value={
                                        selectedCycle
                                          ? {
                                              value: selectedCycle,
                                              label: selectedCycle,
                                            }
                                          : null
                                      }
                                    />
                                  </div>
                                </Col>
                              </Row>

                              <Row className="mb-4">
                                <Col xl="12">
                                  <div className="table-responsive">
                                    <BootstrapTable
                                      {...toolkitprops.baseProps}
                                      {...paginationTableProps}
                                      defaultSorted={[
                                        {
                                          dataField: "id",
                                          order: "desc",
                                        },
                                      ]}
                                      classes={"table align-middle table-hover"}
                                      bordered={false}
                                      striped={true}
                                      headerWrapperClasses={"table-light"}
                                      responsive
                                      data={filteredData}
                                      rowStyle={this.getRowStyle}
                                    />
                                  </div>
                                </Col>
                              </Row>
                              <Row className="align-items-md-center mt-30">
                                <Col className="pagination pagination-rounded justify-content-end mb-2">
                                  <PaginationListStandalone
                                    {...paginationProps}
                                  />
                                </Col>
                              </Row>
                            </React.Fragment>
                          )}
                        </ToolkitProvider>
                      )}
                    </PaginationProvider>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

Roundural.propTypes = {
  match: PropTypes.object,
  CycleList: PropTypes.array,
  SelectedSchemeList: PropTypes.array,
  onGetCycleList: PropTypes.func,
  className: PropTypes.any,
  onGetRoundList: PropTypes.func,
  error: PropTypes.any,
  success: PropTypes.any,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  SelectedSchemeList:
    state.SelectedSchemeList?.SelectedSchemeList?.map((item) => ({
      ...item,
      cycle_status: item.cycle_status || "Unknown",
    })) || [],
  CycleList: state.CycleList || [],
});

const mapDispatchToProps = (dispatch) => ({
  onGetRoundList: (id) => dispatch(getSelectedSchemesList(id)),
  onGetCycleList: (id) => dispatch(getcyclelist(id)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(Roundural));
